import type { ListMCPServersParams } from '@kirin/api-client/api/mcp';
import type { MCPEnforcementView } from '@kirin/api-client/contracts/mcp';
import type {
  ListShadowAiToolsParams,
  ListRunningProcessesParams,
  ListNetworkConnectionsParams,
} from '@kirin/api-client/api/shadow-ai';
import type { ListUserSecurityPostureParams } from '@kirin/api-client/api/user-security-posture';
import type { FilterValue } from '@kirin/api-client/contracts/table';
import type { BulkAlertSelection } from '@kirin/api-client/contracts/alerts';
import type {
  RunningNowFilterOptionField,
  RunningNowSortField,
} from '@kirin/api-client/contracts/shadow-ai-running-now';
import type { PersonalInstalledToolsFilterOptionField } from '@kirin/api-client/contracts/account-shadow-ai-installed-tools';
import type { PersonalBrowserAiToolsFilterOptionField } from '@kirin/api-client/contracts/account-shadow-ai-browser-tools';
import type { SortOrder } from '@kirin/api-client/contracts/helpers';
import type {
  ToolApprovalFilter,
  ToolApprovalTab,
} from '@kirin/api-client/contracts/shadow-ai-tool-approvals';

/**
 * Query keys for React Query
 * These keys are used to identify and cache API requests
 */

export const queryKeys = {
  // Account queries
  account: {
    whoami: ['account', 'whoami'] as const,
    apiKeyStatus: ['account', 'apiKeyStatus'] as const,
    // KIR-3252 personal-scope bulk-action eligibility preview. Lives under the
    // `alerts` head (same namespace as the account grouped-alerts list) so the
    // cache layer can invalidate every selection by predicate; `selection`
    // distinguishes cache entries.
    bulkAlertEligibility: (selection: BulkAlertSelection) =>
      ['alerts', 'bulkAlertEligibility', selection] as const,
    // KIR-2788: personal notification-landing resolve (no org).
    alertRef: (token: string) => ['account', 'alertRef', token] as const,
  },

  // Organization queries
  organizations: {
    all: ['organizations'] as const,
    list: () => [...queryKeys.organizations.all, 'list'] as const,
    inviteToken: (token: string) =>
      [...queryKeys.organizations.all, 'inviteToken', token] as const,
    detail: (id: string) =>
      [...queryKeys.organizations.all, 'detail', id] as const,
    members: (id: string) =>
      [...queryKeys.organizations.all, 'members', id] as const,
    personalKeyMembers: (id: string) =>
      [...queryKeys.organizations.all, 'personalKeyMembers', id] as const,
    apiKeyStatus: (id: string) =>
      [...queryKeys.organizations.all, 'apiKeyStatus', id] as const,
    organizationLevelApiKeyStatus: (id: string) =>
      [
        ...queryKeys.organizations.all,
        'organizationLevelApiKeyStatus',
        id,
      ] as const,
    pluginApiKeys: (id: string) =>
      [...queryKeys.organizations.all, 'pluginApiKeys', id] as const,
    managementApiKeys: (id: string) =>
      [...queryKeys.organizations.all, 'managementApiKeys', id] as const,
    scimProvisioning: (id: string) =>
      [...queryKeys.organizations.all, 'scimProvisioning', id] as const,
    dashboardAlerts: (
      organizationId: string,
      params: { from: string; to: string; tz?: string }
    ) =>
      [
        ...queryKeys.organizations.all,
        'dashboardAlerts',
        { organizationId, ...params },
      ] as const,
    dashboardUsage: (
      organizationId: string,
      params: { from: string; to: string; tz?: string }
    ) =>
      [
        ...queryKeys.organizations.all,
        'dashboardUsage',
        { organizationId, ...params },
      ] as const,
    dashboardTopAlerts: (
      organizationId: string,
      params: { from: string; to: string; limit?: number }
    ) =>
      [
        ...queryKeys.organizations.all,
        'dashboardTopAlerts',
        { organizationId, ...params },
      ] as const,
    // KIR-5075: AI Risk Map tabs.
    riskMapFlow: (
      organizationId: string,
      params: { from: string; to: string; dataset: string }
    ) =>
      [
        ...queryKeys.organizations.all,
        'riskMapFlow',
        { organizationId, ...params },
      ] as const,
    riskMapBlastRadius: (
      organizationId: string,
      params: { from: string; to: string }
    ) =>
      [
        ...queryKeys.organizations.all,
        'riskMapBlastRadius',
        { organizationId, ...params },
      ] as const,
    riskMapRadar: (organizationId: string, params: { from: string }) =>
      [
        ...queryKeys.organizations.all,
        'riskMapRadar',
        { organizationId, ...params },
      ] as const,
    groupedAlerts: (
      organizationId: string,
      params?: {
        page?: number;
        limit?: number;
        sortBy?: string;
        sortOrder?: string;
        filters?: FilterValue[];
        search?: string;
      }
    ) =>
      [
        ...queryKeys.organizations.all,
        'groupedAlerts',
        { organizationId, ...params },
      ] as const,
    alertOccurrences: (
      organizationId: string,
      alertKey: string,
      params?: { page?: number; limit?: number }
    ) =>
      [
        ...queryKeys.organizations.all,
        'alertOccurrences',
        { organizationId, alertKey, ...params },
      ] as const,
    // KIR-2788: unified notification landing — resolves one token to the exact
    // alert(s) it pointed at (a finding_key or an alert-set id).
    alertRef: (organizationId: string, token: string) =>
      [
        ...queryKeys.organizations.all,
        'alertRef',
        { organizationId, token },
      ] as const,
    // KIR-3252 bulk-action eligibility preview. The `{ organizationId }` segment
    // is stable so all selections for an org can be invalidated by predicate;
    // `selection` distinguishes cache entries per selection.
    bulkAlertEligibility: (
      organizationId: string,
      selection: BulkAlertSelection
    ) =>
      [
        ...queryKeys.organizations.all,
        'bulkAlertEligibility',
        { organizationId },
        selection,
      ] as const,
  },

  // Policy queries
  policies: {
    all: ['policies'] as const,
    user: ['policies', 'user'] as const,
    organization: (id: string) => ['policies', 'organization', id] as const,
    settings: (policyKey: string) =>
      ['policies', 'settings', policyKey] as const,
    organizationSettings: (orgId: string, policyKey: string) =>
      ['policies', 'organization', orgId, 'settings', policyKey] as const,
    // KIR-3800: security-group-scoped policies. Kept under the same `policies`
    // root so a broad invalidate still reaches both scopes.
    group: (orgId: string, groupId: string) =>
      ['policies', 'organization', orgId, 'group', groupId] as const,
    groupSettings: (orgId: string, groupId: string, policyKey: string) =>
      [
        'policies',
        'organization',
        orgId,
        'group',
        groupId,
        'settings',
        policyKey,
      ] as const,
  },

  // Security groups (KIR-3800)
  securityGroups: {
    all: ['securityGroups'] as const,
    list: (orgId: string) => ['securityGroups', orgId] as const,
    detail: (orgId: string, groupId: string) =>
      ['securityGroups', orgId, groupId] as const,
    members: (orgId: string, groupId: string) =>
      ['securityGroups', orgId, groupId, 'members'] as const,
    // The caller's own group (KIR-4334). Under the same `securityGroups` prefix
    // so the existing `all` invalidations (add/remove member, delete group)
    // refresh it too.
    mine: (orgId: string) => ['securityGroups', orgId, 'mine'] as const,
  },

  // Allow/block rule queries (policy exceptions API)
  policyExceptions: {
    all: ['policyExceptions'] as const,
    user: (policyKey: string, action?: string) =>
      ['policyExceptions', 'user', policyKey, { action }] as const,
    organization: (orgId: string, policyKey: string, action?: string) =>
      [
        'policyExceptions',
        'organization',
        orgId,
        policyKey,
        { action },
      ] as const,
  },

  // Trusted MCP servers (KIR-2376) — read-only, system-managed
  trustedMcps: {
    all: ['trustedMcps'] as const,
    list: () => [...queryKeys.trustedMcps.all, 'list'] as const,
  },

  // Slack settings queries
  slackSettings: {
    all: ['slackSettings'] as const,
    user: () => [...queryKeys.slackSettings.all, 'user'] as const,
    organization: (id: string) =>
      [...queryKeys.slackSettings.all, 'organization', id] as const,
  },

  // SSO settings queries
  ssoSettings: {
    all: ['ssoSettings'] as const,
    organization: (id: string) =>
      [...queryKeys.ssoSettings.all, 'organization', id] as const,
  },

  // Account (user) settings by type (e.g. enforcement-severity-threshold)
  accountSettings: {
    all: ['accountSettings'] as const,
    detail: (settingType: string) =>
      [...queryKeys.accountSettings.all, 'detail', settingType] as const,
  },

  // User preferences (theme, etc.) — boot-time GET + per-key mutations share this key
  preferences: {
    all: ['preferences'] as const,
  },

  // Organization settings by type (generic: SIEM, etc.)
  orgSettings: {
    all: ['orgSettings'] as const,
    detail: (organizationId: string, settingType: string) =>
      [
        ...queryKeys.orgSettings.all,
        'detail',
        organizationId,
        settingType,
      ] as const,
  },

  // KIR-5191: agent auto-update (org switch)
  agentAutoUpdate: {
    all: ['agentAutoUpdate'] as const,
    setting: (organizationId: string) =>
      [...queryKeys.agentAutoUpdate.all, 'setting', organizationId] as const,
  },

  // Notification settings queries
  notificationSettings: {
    all: ['notificationSettings'] as const,
    user: () => [...queryKeys.notificationSettings.all, 'user'] as const,
    organization: (id: string) =>
      [...queryKeys.notificationSettings.all, 'organization', id] as const,
  },

  // Alert aggregation settings queries
  alertAggregationSettings: {
    all: ['alertAggregationSettings'] as const,
    user: () => [...queryKeys.alertAggregationSettings.all, 'user'] as const,
    organization: (id: string) =>
      [...queryKeys.alertAggregationSettings.all, 'organization', id] as const,
  },

  // YARA exclusions queries
  yaraExclusions: {
    all: ['yaraExclusions'] as const,
    user: () => [...queryKeys.yaraExclusions.all, 'user'] as const,
    organization: (id: string) =>
      [...queryKeys.yaraExclusions.all, 'organization', id] as const,
  },

  // MCP servers queries
  mcp: {
    all: ['mcp'] as const,
    list: (organizationId?: string, params?: ListMCPServersParams) =>
      [...queryKeys.mcp.all, 'list', { organizationId, ...params }] as const,
    personal: (params?: ListMCPServersParams) =>
      [...queryKeys.mcp.all, 'personal', { ...params }] as const,
    users: (organizationId?: string, serverName?: string) =>
      [...queryKeys.mcp.all, 'users', { organizationId, serverName }] as const,
    // KIR-5108: the AgentMesh risk card for one row.
    risk: (organizationId: string, name: string) =>
      [...queryKeys.mcp.all, 'risk', { organizationId, name }] as const,
    dataTable: (
      organizationId: string,
      params?: {
        page?: number;
        limit?: number;
        sortBy?: string;
        sortOrder?: string;
        filters?: unknown[];
        search?: string;
        // KIR-4482 New / Blocked chip.
        enforcementView?: MCPEnforcementView | null;
      }
    ) =>
      [
        ...queryKeys.mcp.all,
        'dataTable',
        { organizationId, ...params },
      ] as const,
    filterOptions: (
      organizationId: string,
      field: string,
      params?: { search?: string; limit?: number }
    ) =>
      [
        ...queryKeys.mcp.all,
        'filter-options',
        organizationId,
        field,
        params,
      ] as const,
    personalDataTable: (params?: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: string;
      filters?: FilterValue[];
      search?: string;
    }) => [...queryKeys.mcp.all, 'personalDataTable', { ...params }] as const,
  },

  // Extensions queries
  extensions: {
    all: ['extensions'] as const,
    personalDataTable: (params?: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: string;
      filters?: FilterValue[];
      search?: string;
    }) =>
      [
        ...queryKeys.extensions.all,
        'personalDataTable',
        { ...params },
      ] as const,
    users: (
      organizationId?: string,
      extensionId?: string,
      version?: { publisher: string; version: string }
    ) =>
      [
        ...queryKeys.extensions.all,
        'users',
        { organizationId, extensionId, ...version },
      ] as const,
    scanResult: (
      organizationId: string | undefined,
      extensionId: string | undefined,
      version: string | undefined
    ) =>
      [
        ...queryKeys.extensions.all,
        'scan-result',
        { organizationId, extensionId, version },
      ] as const,
    orgDataTable: (
      organizationId: string,
      params?: {
        page?: number;
        limit?: number;
        sortBy?: string;
        sortOrder?: string;
        filters?: FilterValue[];
        search?: string;
      }
    ) =>
      [
        ...queryKeys.extensions.all,
        'orgDataTable',
        { organizationId, ...params },
      ] as const,
    orgFilterOptions: (
      organizationId: string,
      field: string,
      params?: { search?: string; limit?: number }
    ) =>
      [
        ...queryKeys.extensions.all,
        'org-filter-options',
        organizationId,
        field,
        params,
      ] as const,
    filterOptions: (
      field: string,
      params?: { search?: string; limit?: number }
    ) =>
      [
        ...queryKeys.extensions.all,
        'personal',
        'filter-options',
        field,
        params,
      ] as const,
  },

  // Plugins queries (Claude-ecosystem plugins: ~/.claude/plugins etc.)
  plugins: {
    all: ['plugins'] as const,
    users: (
      organizationId?: string,
      identity?: { pluginName: string; marketplace: string | null } | null
    ) =>
      [
        ...queryKeys.plugins.all,
        'users',
        {
          organizationId,
          pluginName: identity?.pluginName,
          marketplace: identity?.marketplace,
        },
      ] as const,
    filterOptions: (
      organizationId: string,
      field: string,
      params?: { search?: string; limit?: number }
    ) =>
      [
        ...queryKeys.plugins.all,
        'filter-options',
        organizationId,
        field,
        params,
      ] as const,
    personalFilterOptions: (
      field: string,
      params?: { search?: string; limit?: number }
    ) =>
      [
        ...queryKeys.plugins.all,
        'personal-filter-options',
        field,
        params,
      ] as const,
  },

  // Shadow AI queries
  shadowAi: {
    all: ['shadowAi'] as const,
    list: (organizationId?: string, params?: ListShadowAiToolsParams) =>
      [
        ...queryKeys.shadowAi.all,
        'list',
        { organizationId, ...params },
      ] as const,
    personal: (params?: ListShadowAiToolsParams) =>
      [...queryKeys.shadowAi.all, 'personal', { ...params }] as const,
    running: (organizationId?: string, params?: ListRunningProcessesParams) =>
      [
        ...queryKeys.shadowAi.all,
        'running',
        { organizationId, ...params },
      ] as const,
    runningPersonal: (params?: ListRunningProcessesParams) =>
      [...queryKeys.shadowAi.all, 'runningPersonal', { ...params }] as const,
    toolUsers: (organizationId?: string, toolId?: string) =>
      [
        ...queryKeys.shadowAi.all,
        'toolUsers',
        { organizationId, toolId },
      ] as const,
    stats: (organizationId?: string) =>
      [...queryKeys.shadowAi.all, 'stats', { organizationId }] as const,
    browserAgentSites: (organizationId?: string) =>
      [
        ...queryKeys.shadowAi.all,
        'browserAgentSites',
        { organizationId },
      ] as const,
    personalStats: (userEmail?: string) =>
      [...queryKeys.shadowAi.all, 'personalStats', { userEmail }] as const,
    networkConnections: (
      organizationId?: string,
      params?: ListNetworkConnectionsParams
    ) =>
      [
        ...queryKeys.shadowAi.all,
        'networkConnections',
        { organizationId, ...params },
      ] as const,
    networkConnectionUsers: (
      organizationId?: string,
      domain?: string | null,
      processName?: string | null,
      // KIR-4948: part of the key, not just the request. Two expanded hosts of
      // one service ask the same URL with different answers, so omitting it
      // would serve the first host's users for every host under the row.
      host?: string | null
    ) =>
      [
        ...queryKeys.shadowAi.all,
        'networkConnectionUsers',
        { organizationId, domain, processName, host },
      ] as const,
    networkDomainHosts: (organizationId?: string, domain?: string | null) =>
      [
        ...queryKeys.shadowAi.all,
        'networkDomainHosts',
        { organizationId, domain },
      ] as const,
    networkStats: (organizationId?: string) =>
      [...queryKeys.shadowAi.all, 'networkStats', { organizationId }] as const,
    networkDomains: (organizationId?: string) =>
      [
        ...queryKeys.shadowAi.all,
        'networkDomains',
        { organizationId },
      ] as const,
    topUsers: (organizationId?: string, limit?: number) =>
      [
        ...queryKeys.shadowAi.all,
        'topUsers',
        { organizationId, limit },
      ] as const,
    topTools: (
      organizationId?: string,
      limit?: number,
      sourceType?: 'installed' | 'browser'
    ) =>
      [
        ...queryKeys.shadowAi.all,
        'topTools',
        { organizationId, limit, sourceType },
      ] as const,
    userInstalledTools: (organizationId?: string, userId?: string | null) =>
      [
        ...queryKeys.shadowAi.all,
        'userInstalledTools',
        { organizationId, userId },
      ] as const,
    userBrowserTools: (organizationId?: string, userId?: string | null) =>
      [
        ...queryKeys.shadowAi.all,
        'userBrowserTools',
        { organizationId, userId },
      ] as const,
    installedAiToolsDataTable: (
      organizationId: string,
      params?: {
        page?: number;
        limit?: number;
        sortBy?: string;
        sortOrder?: string;
        filters?: unknown[];
        search?: string;
        approval?: ToolApprovalFilter;
      }
    ) =>
      [
        ...queryKeys.shadowAi.all,
        'installedAiToolsDataTable',
        { organizationId, ...params },
      ] as const,
    installedAiToolsFilterOptions: (
      organizationId: string,
      field: string,
      params?: { search?: string; limit?: number; offset?: number }
    ) =>
      [
        ...queryKeys.shadowAi.all,
        'installedAiToolsFilterOptions',
        organizationId,
        field,
        params,
      ] as const,
    personalInstalledAiToolsDataTable: (params?: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: string;
      filters?: unknown[];
      search?: string;
      approval?: string;
    }) =>
      [
        ...queryKeys.shadowAi.all,
        'personalInstalledAiToolsDataTable',
        { ...params },
      ] as const,
    personalInstalledAiToolsFilterOptions: (
      field: PersonalInstalledToolsFilterOptionField,
      params?: { search?: string; limit?: number; offset?: number }
    ) =>
      [
        ...queryKeys.shadowAi.all,
        'personalInstalledAiToolsFilterOptions',
        field,
        params,
      ] as const,
    browserAiToolsDataTable: (
      organizationId: string,
      params?: {
        page?: number;
        limit?: number;
        sortBy?: string;
        sortOrder?: string;
        filters?: unknown[];
        search?: string;
        navigationType?: string;
        minVisits?: number;
        approval?: ToolApprovalFilter;
      }
    ) =>
      [
        ...queryKeys.shadowAi.all,
        'browserAiToolsDataTable',
        { organizationId, ...params },
      ] as const,
    browserAiToolsFilterOptions: (
      organizationId: string,
      field: string,
      params?: { search?: string; limit?: number }
    ) =>
      [
        ...queryKeys.shadowAi.all,
        'browserAiToolsFilterOptions',
        organizationId,
        field,
        params,
      ] as const,
    // KIR-4854: Show-filter tab totals (independent of search/column filters).
    toolApprovalCounts: (
      organizationId: string,
      tab: ToolApprovalTab,
      scopeUserId?: string
    ) =>
      [
        ...queryKeys.shadowAi.all,
        'toolApprovalCounts',
        { organizationId, tab, scopeUserId },
      ] as const,
    // KIR-4864: personal-account Show-filter totals (no org). Keyed on the
    // user so a switched account never reads the prior user's totals.
    personalToolApprovalCounts: (tab: ToolApprovalTab, userEmail?: string) =>
      [
        ...queryKeys.shadowAi.all,
        'personalToolApprovalCounts',
        { tab, userEmail },
      ] as const,
    // KIR-3496: Network Discovery DataTable (per-column filter/sort + top-level
    // status scope).
    networkDomainsDataTable: (
      organizationId: string,
      params?: {
        page?: number;
        limit?: number;
        status?: string;
        sortBy?: string;
        sortOrder?: string;
        filters?: unknown[];
        search?: string;
      }
    ) =>
      [
        ...queryKeys.shadowAi.all,
        'networkDomainsDataTable',
        { organizationId, ...params },
      ] as const,
    networkConnectionsFilterOptions: (
      organizationId: string,
      field: string,
      params?: {
        search?: string;
        limit?: number;
        offset?: number;
        status?: string;
      }
    ) =>
      [
        ...queryKeys.shadowAi.all,
        'networkConnectionsFilterOptions',
        organizationId,
        field,
        params,
      ] as const,
    personalBrowserAiToolsDataTable: (params?: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: string;
      filters?: unknown[];
      search?: string;
      approval?: string;
    }) =>
      [
        ...queryKeys.shadowAi.all,
        'personalBrowserAiToolsDataTable',
        { ...params },
      ] as const,
    personalBrowserAiToolsFilterOptions: (
      field: PersonalBrowserAiToolsFilterOptionField,
      params?: { search?: string; limit?: number; offset?: number }
    ) =>
      [
        ...queryKeys.shadowAi.all,
        'personalBrowserAiToolsFilterOptions',
        field,
        params,
      ] as const,
  },

  // Running Now DataTable queries
  runningNow: {
    all: ['runningNow'] as const,
    dataTable: (params: {
      organizationId: string;
      page?: number;
      limit?: number;
      filters?: FilterValue[];
      sortBy?: RunningNowSortField;
      sortOrder?: SortOrder;
      search?: string;
      approval?: ToolApprovalFilter;
    }) => [...queryKeys.runningNow.all, 'dataTable', { ...params }] as const,
    filterOptions: (
      organizationId: string,
      field: RunningNowFilterOptionField,
      params: { search?: string; limit?: number; offset?: number }
    ) =>
      [
        ...queryKeys.runningNow.all,
        'filterOptions',
        organizationId,
        field,
        params,
      ] as const,
  },

  // User security posture queries
  userSecurityPosture: {
    all: ['userSecurityPosture'] as const,
    list: (organizationId?: string, params?: ListUserSecurityPostureParams) =>
      [
        ...queryKeys.userSecurityPosture.all,
        'list',
        { organizationId, ...params },
      ] as const,
    dataTable: (
      organizationId: string,
      params?: {
        page?: number;
        limit?: number;
        sortBy?: string;
        sortOrder?: string;
        filters?: FilterValue[];
        search?: string;
      }
    ) =>
      [
        ...queryKeys.userSecurityPosture.all,
        'dataTable',
        { organizationId, ...params },
      ] as const,
    filterOptions: (
      organizationId: string,
      field: string,
      params?: { search?: string; limit?: number; offset?: number }
    ) =>
      [
        ...queryKeys.userSecurityPosture.all,
        'filterOptions',
        { organizationId, field, ...params },
      ] as const,
  },

  // Projects inventory queries (KIR-3356)
  projects: {
    all: ['projects'] as const,
    dataTable: (
      organizationId: string,
      params?: {
        page?: number;
        limit?: number;
        sortBy?: string;
        sortOrder?: string;
        filters?: FilterValue[];
        search?: string;
      }
    ) =>
      [
        ...queryKeys.projects.all,
        'dataTable',
        { organizationId, ...params },
      ] as const,
    detail: (organizationId: string, slug: string) =>
      [...queryKeys.projects.all, 'detail', { organizationId, slug }] as const,
    coverage: (organizationId: string, slug: string) =>
      [
        ...queryKeys.projects.all,
        'coverage',
        { organizationId, slug },
      ] as const,
    filterOptions: (
      organizationId: string,
      field: string,
      params: { search?: string; limit?: number; offset?: number }
    ) =>
      [
        ...queryKeys.projects.all,
        'filterOptions',
        organizationId,
        field,
        params,
      ] as const,
  },

  // Grouped alerts queries (account-level)
  alerts: {
    all: ['alerts'] as const,
    list: (params?: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: string;
    }) => ['alerts', 'list', params] as const,
    listFiltered: (params?: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: string;
      filters?: FilterValue[];
      search?: string;
    }) => ['alerts', 'listFiltered', params] as const,
    occurrences: (
      alertKey: string,
      params?: { page?: number; limit?: number }
    ) => ['alerts', 'occurrences', alertKey, params] as const,
    filterOptions: (
      field: string,
      params?: { search?: string; limit?: number }
    ) => ['alerts', 'filter-options', field, params] as const,
  },

  // Network Discovery queries
  networkDiscovery: {
    all: ['networkDiscovery'] as const,
    orgIgnore: (organizationId?: string) =>
      [...queryKeys.networkDiscovery.all, 'orgIgnore', organizationId] as const,
  },

  // AI Instructions queries
  aiInstructions: {
    all: ['aiInstructions'] as const,

    dataTable: (
      organizationId: string,
      params?: {
        page?: number;
        limit?: number;
        sortBy?: string;
        sortOrder?: string;
        filters?: FilterValue[];
        search?: string;
      }
    ) =>
      [
        ...queryKeys.aiInstructions.all,
        'dataTable',
        { organizationId, ...params },
      ] as const,

    filterOptions: (
      organizationId: string,
      field: string,
      params?: { search?: string; limit?: number; offset?: number }
    ) =>
      [
        ...queryKeys.aiInstructions.all,
        'filter-options',
        { organizationId, field, ...params },
      ] as const,
  },

  // KIR-4699 — Inventory → Skills queries. Organization scope only; there is
  // no account-scoped skills route.
  skills: {
    all: ['skills'] as const,

    dataTable: (
      organizationId: string,
      params?: {
        page?: number;
        limit?: number;
        sortBy?: string;
        sortOrder?: string;
        filters?: FilterValue[];
        search?: string;
        listState?: string;
      }
    ) =>
      [
        ...queryKeys.skills.all,
        'dataTable',
        { organizationId, ...params },
      ] as const,

    filterOptions: (
      organizationId: string,
      field: string,
      params?: {
        search?: string;
        limit?: number;
        offset?: number;
        listState?: string;
      }
    ) =>
      [
        ...queryKeys.skills.all,
        'filterOptions',
        { organizationId, field, ...params },
      ] as const,
  },

  // KIR-2917 — Hook Inventory queries
  hooks: {
    all: ['hooks'] as const,
    dataTable: (
      organizationId: string,
      params?: {
        page?: number;
        limit?: number;
        sortBy?: string;
        sortOrder?: string;
        filters?: FilterValue[];
        search?: string;
      }
    ) =>
      [
        ...queryKeys.hooks.all,
        'dataTable',
        { organizationId, ...params },
      ] as const,
    filterOptions: (
      organizationId: string,
      field: string,
      params?: { search?: string; limit?: number; offset?: number }
    ) =>
      [
        ...queryKeys.hooks.all,
        'filter-options',
        { organizationId, field, ...params },
      ] as const,
    content: (organizationId: string, groupKey: string) =>
      [...queryKeys.hooks.all, 'content', organizationId, groupKey] as const,
    users: (organizationId: string, groupKey: string) =>
      [...queryKeys.hooks.all, 'users', organizationId, groupKey] as const,
    projects: (organizationId: string, groupKey: string) =>
      [...queryKeys.hooks.all, 'projects', organizationId, groupKey] as const,
  },
  // KIR-4445: agent session timeline, keyed by alert occurrence. A null
  // organizationId is the personal-account scope.
  sessionTimeline: {
    all: ['sessionTimeline'] as const,
    byOccurrence: (organizationId: string | null, occurrenceId: string) =>
      [
        ...queryKeys.sessionTimeline.all,
        { organizationId, occurrenceId },
      ] as const,
    summary: (organizationId: string | null, occurrenceId: string) =>
      [
        ...queryKeys.sessionTimeline.byOccurrence(organizationId, occurrenceId),
        'summary',
      ] as const,
    // Each drawer opening (viewId) is its own query; omit it to match them all.
    timeline: (
      organizationId: string | null,
      occurrenceId: string,
      viewId?: string
    ) =>
      [
        ...queryKeys.sessionTimeline.byOccurrence(organizationId, occurrenceId),
        'timeline',
        ...(viewId ? [viewId] : []),
      ] as const,
    event: (
      organizationId: string | null,
      occurrenceId: string,
      eventId: string
    ) =>
      [
        ...queryKeys.sessionTimeline.byOccurrence(organizationId, occurrenceId),
        'event',
        eventId,
      ] as const,
  },
  // KIR-5155: in-app help. The docs manifest is a static file, the same for
  // every organization and user.
  help: {
    manifest: ['help', 'manifest'] as const,
  },
} as const;
